<template>
    <div class="ops-templates">
        <div class="page-title d-flex justify-content-between align-items-center">
            <div class="title">
                <h3>Checklist templates</h3>
            </div>
            <el-button type="primary" size="small" @click="openCreate">New template</el-button>
        </div>

        <el-row :gutter="16">
            <el-col :span="8">
                <el-card shadow="never" v-loading="loadingList">
                    <div
                        v-for="t in templates"
                        :key="t.id"
                        class="tpl-row"
                        :class="{ active: selected && selected.id === t.id }"
                        @click="selectTemplate(t.id)"
                    >
                        <strong>{{ t.name }}</strong>
                        <div class="small text-muted">
                            {{ t.items_count }} items
                            <el-tag v-if="t.is_default" size="small" type="success" class="ml-1">Default</el-tag>
                            <el-tag v-if="!t.is_active" size="small" type="info" class="ml-1">Inactive</el-tag>
                        </div>
                    </div>
                </el-card>
            </el-col>
            <el-col :span="16">
                <el-card v-if="selected" shadow="never" v-loading="loadingDetail">
                    <el-form label-position="top">
                        <el-row :gutter="12">
                            <el-col :span="12">
                                <el-form-item label="Name">
                                    <el-input v-model="selected.name" />
                                </el-form-item>
                            </el-col>
                            <el-col :span="12">
                                <el-form-item label="Package key">
                                    <el-input v-model="selected.package_key" />
                                </el-form-item>
                            </el-col>
                        </el-row>
                        <el-form-item>
                            <el-checkbox v-model="selected.is_default">Default template</el-checkbox>
                            <el-checkbox v-model="selected.is_active" class="ml-3">Active</el-checkbox>
                        </el-form-item>
                        <el-button type="primary" size="small" :loading="saving" @click="saveTemplate">Save template</el-button>
                    </el-form>

                    <el-divider>Items</el-divider>
                    <el-button size="small" class="mb-2" @click="openItem()">Add item</el-button>
                    <el-table :data="selected.items" size="small" border max-height="480">
                        <el-table-column prop="stage" label="Stage" width="100" />
                        <el-table-column prop="title" label="Title" min-width="220" />
                        <el-table-column prop="owner_role" label="Owner" width="110" />
                        <el-table-column prop="due_rule" label="Due" width="120" />
                        <el-table-column prop="weight" label="W" width="50" />
                        <el-table-column label="Crit" width="60">
                            <template #default="scope">
                                <el-tag v-if="scope.row.is_critical" type="danger" size="small">Y</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column prop="applies_when" label="When" width="120" />
                        <el-table-column prop="item_key" label="Key" width="140" show-overflow-tooltip />
                        <el-table-column label="" width="120" fixed="right">
                            <template #default="scope">
                                <el-button size="small" @click="openItem(scope.row)">Edit</el-button>
                                <el-button size="small" type="danger" @click="removeItem(scope.row)">Del</el-button>
                            </template>
                        </el-table-column>
                    </el-table>
                </el-card>
                <el-empty v-else description="Select a template" />
            </el-col>
        </el-row>

        <el-dialog v-model="itemDialog" :title="itemForm.id ? 'Edit item' : 'Add item'" width="560px">
            <el-form label-position="top">
                <el-form-item label="Stage">
                    <el-select v-model="itemForm.stage" style="width: 100%" filterable>
                        <el-option
                            v-for="s in stageOptions"
                            :key="s.value"
                            :label="s.label"
                            :value="s.value"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="Title"><el-input v-model="itemForm.title" /></el-form-item>
                <el-form-item label="Owner role">
                    <el-select v-model="itemForm.owner_role" style="width: 100%">
                        <el-option
                            v-for="role in ownerRoleOptions"
                            :key="role.value"
                            :label="role.label"
                            :value="role.value"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="Due rule">
                    <el-select
                        v-model="itemForm.due_rule"
                        style="width: 100%"
                        filterable
                        allow-create
                        default-first-option
                        placeholder="Select or type e.g. booking+1"
                    >
                        <el-option
                            v-for="rule in dueRuleOptions"
                            :key="rule"
                            :label="dueRuleLabel(rule)"
                            :value="rule"
                        />
                    </el-select>
                    <div class="form-hint">Presets cover common rules; you can still type a custom rule.</div>
                </el-form-item>
                <el-form-item label="Applies when">
                    <el-select v-model="itemForm.applies_when" style="width: 100%" filterable>
                        <el-option
                            v-for="opt in appliesWhenOptions"
                            :key="opt.value"
                            :label="opt.label + ' (' + opt.value + ')'"
                            :value="opt.value"
                        />
                    </el-select>
                </el-form-item>
                <el-form-item label="Item key">
                    <el-select
                        v-model="itemForm.item_key"
                        style="width: 100%"
                        filterable
                        allow-create
                        default-first-option
                        placeholder="Select sync/UI key"
                    >
                        <el-option-group label="Special UI families">
                            <el-option
                                v-for="key in specialItemKeys"
                                :key="'special-' + key"
                                :label="key"
                                :value="key"
                            />
                        </el-option-group>
                        <el-option-group label="Template / sync keys">
                            <el-option
                                v-for="key in syncItemKeys"
                                :key="'sync-' + key"
                                :label="key"
                                :value="key"
                            />
                        </el-option-group>
                    </el-select>
                    <div class="form-hint">
                        Stable key used for enquiry sync. Prefer existing keys; type a new snake_case key only when adding a new task type.
                    </div>
                </el-form-item>
                <el-form-item label="Weight"><el-input-number v-model="itemForm.weight" :min="1" /></el-form-item>
                <el-checkbox v-model="itemForm.is_critical">Critical</el-checkbox>
            </el-form>
            <template #footer>
                <el-button @click="itemDialog = false">Cancel</el-button>
                <el-button type="primary" :loading="savingItem" @click="saveItem">Save</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import axios from 'axios';
import { ElMessage, ElMessageBox } from 'element-plus';

const templates = ref([]);
const selected = ref(null);
const loadingList = ref(false);
const loadingDetail = ref(false);
const saving = ref(false);
const savingItem = ref(false);
const itemDialog = ref(false);

const stageOptions = ref([
    { value: 'booking', label: 'Booking' },
    { value: 'fulfilment', label: 'Fulfilment' },
    { value: 'qa', label: 'QA' },
    { value: 'pre_trip', label: 'Pre-trip' },
    { value: 'in_trip', label: 'In-trip' },
    { value: 'closure', label: 'Closure' },
]);
const ownerRoleOptions = ref([
    { value: 'Operation', label: 'Operation' },
    { value: 'Account', label: 'Account' },
    { value: 'Sales', label: 'Sales' },
    { value: 'assigned_to', label: 'Trip owner (assigned_to)' },
]);
const appliesWhenOptions = ref([
    { value: 'always', label: 'Always' },
    { value: 'is_hotel', label: 'Hotel enquiry' },
    { value: 'is_flight', label: 'Flight enquiry' },
    { value: 'is_landpackage', label: 'Land package enquiry' },
    { value: 'is_visa', label: 'Visa enquiry' },
    { value: 'is_cruise', label: 'Cruise enquiry' },
    { value: 'is_insurance', label: 'Insurance enquiry' },
    { value: 'is_meal', label: 'Meal included' },
    { value: 'is_hotel_or_land', label: 'Hotel or land package' },
    { value: 'is_visa_or_flight', label: 'Visa or flight' },
    { value: 'has_helicopter', label: 'Has helicopter' },
    { value: 'has_train_or_bus', label: 'Has train or bus tickets' },
]);
const dueRuleOptions = ref([
    'booking+0', 'booking+1', 'booking+2', 'booking+3',
    'departure-21', 'departure-14', 'departure-7', 'departure-5', 'departure-3', 'departure-2', 'departure-1',
    'return+1', 'return+2', 'return+3', 'return+7',
    'milestone:arrival', 'milestone:checkin', 'milestone:transfer', 'milestone:tour',
    'milestone:mid_trip', 'milestone:dep_eve', 'milestone:departure', 'milestone:as_needed',
]);
const itemKeyOptions = ref([
    'standard', 'confirmation_call', 'driver_details', 'customer_issue', 'incident',
]);

const SPECIAL_ITEM_KEYS = ['standard', 'confirmation_call', 'driver_details', 'customer_issue', 'incident'];

const itemForm = reactive({
    id: null,
    stage: 'booking',
    title: '',
    owner_role: 'Operation',
    due_rule: 'booking+1',
    applies_when: 'always',
    item_key: 'standard',
    weight: 1,
    is_critical: false,
});

const specialItemKeys = computed(() => {
    const keys = new Set(SPECIAL_ITEM_KEYS);
    itemKeyOptions.value.forEach((key) => {
        if (
            key === 'standard'
            || key === 'confirmation_call'
            || key.startsWith('confirmation_call_')
            || key === 'driver_details'
            || key.startsWith('driver_details_')
            || key === 'customer_issue'
            || key.startsWith('customer_issue_')
            || key === 'incident'
            || key.startsWith('incident_')
        ) {
            keys.add(key);
        }
    });
    if (itemForm.item_key) keys.add(itemForm.item_key);
    return Array.from(keys).sort();
});

const syncItemKeys = computed(() => {
    const special = new Set(specialItemKeys.value);
    const keys = itemKeyOptions.value.filter((key) => !special.has(key));
    if (itemForm.item_key && !special.has(itemForm.item_key) && !keys.includes(itemForm.item_key)) {
        keys.push(itemForm.item_key);
    }
    return keys.sort();
});

const dueRuleLabel = (rule) => {
    if (rule.startsWith('booking+')) return `Booking + ${rule.slice(8)} day(s)`;
    if (rule.startsWith('departure-')) return `Departure − ${rule.slice(10)} day(s)`;
    if (rule.startsWith('return+')) return `Return + ${rule.slice(7)} day(s)`;
    if (rule.startsWith('milestone:')) return `Milestone: ${rule.slice(10)}`;
    return rule;
};

const applyFormOptions = (options = {}) => {
    if (Array.isArray(options.stages) && options.stages.length) {
        stageOptions.value = options.stages;
    }
    if (Array.isArray(options.owner_roles) && options.owner_roles.length) {
        ownerRoleOptions.value = options.owner_roles;
    }
    if (Array.isArray(options.applies_when) && options.applies_when.length) {
        appliesWhenOptions.value = options.applies_when;
    }
    if (Array.isArray(options.due_rules) && options.due_rules.length) {
        dueRuleOptions.value = options.due_rules;
    }
    if (Array.isArray(options.item_keys) && options.item_keys.length) {
        itemKeyOptions.value = options.item_keys;
    }
};

const loadList = async () => {
    loadingList.value = true;
    try {
        const { data } = await axios.get('/operations/templates/list');
        templates.value = data.data || [];
        applyFormOptions(data.form_options || {});
        if (!data.form_options && Array.isArray(data.stages) && data.stages.length) {
            stageOptions.value = data.stages.map((s) => ({
                value: s,
                label: s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
            }));
        }
    } finally {
        loadingList.value = false;
    }
};

const selectTemplate = async (id) => {
    loadingDetail.value = true;
    try {
        const { data } = await axios.get('/operations/templates/' + id);
        selected.value = data.data;
        selected.value.is_default = !!selected.value.is_default;
        selected.value.is_active = !!selected.value.is_active;
    } finally {
        loadingDetail.value = false;
    }
};

const openCreate = async () => {
    const name = window.prompt('Template name');
    if (!name) return;
    const { data } = await axios.post('/operations/templates', { name, is_active: true });
    await loadList();
    if (data.data?.id) selectTemplate(data.data.id);
};

const saveTemplate = async () => {
    saving.value = true;
    try {
        await axios.post('/operations/templates/' + selected.value.id, {
            name: selected.value.name,
            package_key: selected.value.package_key,
            is_default: selected.value.is_default,
            is_active: selected.value.is_active,
            target_curve: selected.value.target_curve,
        });
        ElMessage.success('Template saved');
        await loadList();
    } finally {
        saving.value = false;
    }
};

const openItem = (row = null) => {
    Object.assign(itemForm, {
        id: row?.id || null,
        stage: row?.stage || 'booking',
        title: row?.title || '',
        owner_role: row?.owner_role || 'Operation',
        due_rule: row?.due_rule || 'booking+1',
        applies_when: row?.applies_when || 'always',
        item_key: row?.item_key || 'standard',
        weight: row?.weight || 1,
        is_critical: !!row?.is_critical,
    });
    if (itemForm.due_rule && !dueRuleOptions.value.includes(itemForm.due_rule)) {
        dueRuleOptions.value = [...dueRuleOptions.value, itemForm.due_rule];
    }
    if (itemForm.item_key && !itemKeyOptions.value.includes(itemForm.item_key)) {
        itemKeyOptions.value = [...itemKeyOptions.value, itemForm.item_key];
    }
    itemDialog.value = true;
};

const saveItem = async () => {
    savingItem.value = true;
    try {
        await axios.post('/operations/templates/' + selected.value.id + '/items', { ...itemForm });
        ElMessage.success('Item saved');
        itemDialog.value = false;
        await selectTemplate(selected.value.id);
        await loadList();
    } finally {
        savingItem.value = false;
    }
};

const removeItem = async (row) => {
    await ElMessageBox.confirm('Delete this item?', 'Confirm');
    await axios.post('/operations/templates/' + selected.value.id + '/items/' + row.id + '/delete');
    await selectTemplate(selected.value.id);
    await loadList();
};

onMounted(loadList);
</script>

<style scoped>
.ops-templates { padding: 12px 4px 40px; }
.tpl-row { padding: 10px; border-bottom: 1px solid #eee; cursor: pointer; }
.tpl-row.active, .tpl-row:hover { background: #f5f7fa; }
.form-hint { margin-top: 6px; font-size: 12px; color: #6c757d; line-height: 1.35; }
</style>
